import { useState, useEffect, useCallback, cloneElement } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Add } from '@mui/icons-material'
import {
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
} from '@mui/material'
import { motion } from 'framer-motion'
import { showSnackbar } from 'functions'
import { Component as HeaderIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom'
import uuid from 'uuid/v4'
import { Paper, CvsDrawer, CvsButton, CvsAutoComplete } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { usePageTitleContext } from '@contexts'
import { patternTypeColors } from '@global/constants'
import { api } from 'api'
import { Preview } from './preview'
import { Segment } from './segment'
import { Autonumber } from './segments/autonumber'
import { Constant } from './segments/constant'
import { DateSeg } from './segments/date'
import { Random } from './segments/random'
import { Variable } from './segments/variable'
import { t } from '@lingui/core/macro'

const ELEMENT_DEFINITIONS = {
  constant: {
    name: 'Constant',
    constant_value: 'CONSTANT',
    segment_type: 'constant',
    data_type: 'string',
  },
  variable: {
    name: 'VAR',
    segment_type: 'variable',
    data_type: 'string',
  },
  date: {
    name: 'Date',
    segment_type: 'date',
    delimiter: '-',
    auto_populate: false,
    date: {
      order: 'days-months-years',
      months: '01-12',
      days: '01-31',
      years: '0000-9999',
    },
  },
  autonumber: {
    name: 'Auto #',
    segment_type: 'autonumber',
    autonumber: {
      min_width: 1,
      start_value: 1,
    },
  },
  random: {
    name: 'Random',
    segment_type: 'random',
    min_length: null,
    max_length: 10,
    data_type: 'string',
    random: {
      frequency: 'row',
      include_numeric: true,
      include_uppercase: true,
      include_lowercase: false,
    },
  },
}

const SortableSegment = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {cloneElement(children, { dragListeners: listeners })}
    </div>
  )
}

export const EditPattern = () => {
  const [unlockedPattern, setUnlockedPattern] = useState(false)
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()
  const patternId = match.params.id === 'new' ? null : match.params.id
  const [pattern, setPattern] = useState({})
  const [loading, setLoading] = useState(true)
  const [columns, setColumns] = useState(4)
  const [visible, setVisible] = useState(false)
  const [requireUniqueness, setRequireUniqueness] = useState(false)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState(null)
  const [blockSelection, setBlockSelection] = useState(false)
  const dispatch = useDispatch()
  const { overridePageTitle } = usePageTitleContext()

  const calculateColumns = () => {
    const elem = document.getElementById('editPattern')
    const cols = Math.floor((elem.clientWidth - 260) / 208)
    setColumns(cols || 4)
  }

  const showRequireUniqueness = useSelector(state => state.Auth.features['require-uniqueness'])
  const unlockedPatternEnabled = useSelector(state => state.Auth.features['unlocked-pattern'])

  useEffect(() => {
    if (!pattern?.name) return
    overridePageTitle('Edit Pattern - Claravine')

    return () => {
      overridePageTitle(null)
    }
  }, [pattern?.name])

  useEffect(() => {
    if (patternId) {
      api.get(`/api/v1/account/field/${patternId}`).then(({ data }) => {
        setPattern({
          ...data,
          segments: data.segments
            .map(seg => ({ ...seg, guid: uuid() }))
            .sort((a, b) => a.segment_precedence - b.segment_precedence),
        })
        setRequireUniqueness(data?.must_be_unique)
        setLoading(false)
      })
    } else if (location.search.includes('duplicate')) {
      const duplicateId = location.search.split('=')[1]
      api.get(`/api/v1/account/field/${duplicateId}`).then(({ data }) => {
        delete data.id
        data.segments.forEach(seg => {
          delete seg.id
          delete seg.segment_parent_field_id
          seg.date && delete seg.date.id
          seg.autonumber && delete seg.autonumber.id
          seg.random && delete seg.random.id
          seg.autonumber && delete seg.autonumber.parent_field_id
          seg.random && delete seg.random.parent_field_id
          seg.date && delete seg.date.parent_field_id
        })
        data.name = `Duplicate of ${data.name}`

        setPattern({
          ...data,
          segments: data.segments
            .map(seg => ({ ...seg, guid: uuid() }))
            .sort((a, b) => a.segment_precedence - b.segment_precedence),
        })
        setLoading(false)
      })
    } else {
      setPattern({ name: '', description: '', delimiter: '', segments: [], is_pattern: true })
      setLoading(false)
    }
    window.addEventListener('resize', calculateColumns)
    calculateColumns()

    return () => {
      window.removeEventListener('resize', calculateColumns)
    }
  }, [])

  const removeSegment = segment => {
    const index = pattern.segments.findIndex(seg => seg.guid === segment.guid)
    const newSegs = [...pattern.segments]
    newSegs.splice(index, 1)
    setPattern({ ...pattern, segments: newSegs })
  }

  const updateSegment = updatedSegment => {
    delete updatedSegment._preview
    const index = pattern.segments.findIndex(seg => seg.guid === updatedSegment.guid)
    const newSegs = [...pattern.segments]
    newSegs.splice(index, 1, updatedSegment)
    setPattern({ ...pattern, segments: newSegs })
    setVisible(false)
  }

  const addSegment = type => {
    const cloned = JSON.parse(JSON.stringify(ELEMENT_DEFINITIONS[type]))
    cloned.guid = uuid()
    setPattern({ ...pattern, segments: [...pattern.segments, cloned] })
  }

  const editSegment = segment => {
    if (blockSelection) {
      return
    }
    setSelectedSegment(segment)
    setVisible(true)
  }

  const savePattern = useCallback(() => {
    setSubmitDisabled(true)
    const patternToSave = {
      ...pattern,
      is_unlocked_pattern: unlockedPattern,
    }

    return api
      .put('/api/v1/account/field/', patternToSave)
      .then(({ data }) => {
        if (!patternId) {
          history.replace(`/admin/patterns/${data.id}`)
        }
        setPattern({
          ...data,
          segments: data.segments
            .map(seg => ({ ...seg, guid: uuid() }))
            .sort((a, b) => a.segment_precedence - b.segment_precedence),
        })
        setSubmitDisabled(false)
        dispatch(
          showSnackbar({
            message: 'Pattern saved successfully',
            type: 'SUCCESS',
          }),
        )
      })
      .catch(() => {
        setSubmitDisabled(false)
        dispatch(
          showSnackbar({
            message: 'Pattern failed to save.',
            type: 'ERROR',
          }),
        )
      })
  }, [patternId, pattern, unlockedPattern])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event

      if (active.id !== over.id) {
        const oldIndex = pattern.segments.findIndex(seg => seg.guid === active.id)
        const newIndex = pattern.segments.findIndex(seg => seg.guid === over.id)
        const newSegments = arrayMove(pattern.segments, oldIndex, newIndex)
        setPattern({ ...pattern, segments: newSegments })
        setBlockSelection(true)
        setTimeout(() => {
          setBlockSelection(false)
        }, 150)
      }
    },
    [pattern],
  )

  return (
    <motion.div
      id="editPattern"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ height: 'calc(100vh - 48px)', padding: 24 }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Patterns`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Manage the taxonomy that will be used to generate your submission tracking codes`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {!loading ? (
          <>
            <Paper
              elevation={3}
              style={{ marginBottom: 12, padding: 12, flexShrink: 1, display: 'flex' }}
            >
              <TextField
                name="name"
                variant="outlined"
                label={t`Name`}
                required={true}
                value={pattern.name ?? ''}
                style={{ marginRight: 12, minWidth: 200, flex: '1' }}
                onChange={e => {
                  setPattern({ ...pattern, name: e.target.value })
                }}
              />
              <TextField
                name="description"
                variant="outlined"
                label={t`Description`}
                style={{ marginRight: 12, minWidth: 200, flex: '1' }}
                value={pattern.description ?? ''}
                onChange={e => {
                  setPattern({ ...pattern, description: e.target.value })
                }}
              />
              <span style={{ display: 'flex', flex: '1' }} />
              <span
                style={{
                  textAlign: 'right',
                  flex: '1',
                  justifyContent: 'flex-end',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CvsButton
                  name="save-pattern"
                  disabled={submitDisabled || !pattern.name}
                  variant="contained"
                  color="primary"
                  onClick={savePattern}
                  size="large"
                  style={{ textAlign: 'right', height: 40 }}
                >
                  {submitDisabled ? 'Saving' : 'Save'}
                </CvsButton>
              </span>
            </Paper>
            <Paper elevation={3} style={{ marginBottom: 12, padding: 12, flexShrink: 1 }}>
              <Preview pattern={pattern} colors={patternTypeColors} />
            </Paper>
            <div style={{ display: 'flex', flexGrow: 1, marginBottom: 12 }}>
              <div style={{ flexShrink: 1, width: 200, marginRight: 12 }}>
                <Paper elevation={3} style={{ padding: 12, marginBottom: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    {t`Elements`}
                  </Typography>
                  <div style={{ marginBottom: 4 }}>
                    <Button
                      name="constant"
                      onClick={() => {
                        addSegment('constant')
                      }}
                      style={{
                        backgroundColor: patternTypeColors.constant,
                        width: '100%',
                        color: 'white',
                      }}
                      startIcon={<Add />}
                    >
                      {t`Constant`}
                    </Button>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Button
                      name="variable"
                      onClick={() => {
                        addSegment('variable')
                      }}
                      style={{
                        backgroundColor: patternTypeColors.variable,
                        width: '100%',
                        color: 'white',
                      }}
                      startIcon={<Add />}
                    >
                      {t`Variable`}
                    </Button>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Button
                      name="autonumber"
                      onClick={() => {
                        addSegment('autonumber')
                      }}
                      style={{
                        backgroundColor: patternTypeColors.autonumber,
                        width: '100%',
                        color: 'white',
                      }}
                      startIcon={<Add />}
                    >
                      {t`Auto #`}
                    </Button>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Button
                      name="date"
                      onClick={() => {
                        addSegment('date')
                      }}
                      style={{
                        backgroundColor: patternTypeColors.date,
                        width: '100%',
                        color: 'white',
                      }}
                      startIcon={<Add />}
                    >
                      {t`Date`}
                    </Button>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Button
                      name="random"
                      onClick={() => {
                        addSegment('random')
                      }}
                      style={{
                        backgroundColor: patternTypeColors.random,
                        width: '100%',
                        color: 'white',
                      }}
                      startIcon={<Add />}
                    >
                      {t`Random`}
                    </Button>
                  </div>
                </Paper>
                <Paper elevation={3} style={{ padding: 12, marginBottom: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    {t`Delimiter`}
                  </Typography>
                  <CvsAutoComplete
                    style={{ margin: 0 }}
                    label={t`Select delimiter`}
                    selected={pattern.delimiter}
                    onSelected={item => {
                      setPattern({ ...pattern, delimiter: item })
                    }}
                    data-testid="delimiter-select"
                    options={[
                      { label: 'None', value: '' },
                      { label: '-', value: '-' },
                      { label: '_', value: '_' },
                      { value: ':', label: ':' },
                      { label: '|', value: '|' },
                    ]}
                  />
                </Paper>

                {unlockedPatternEnabled && (
                  <Paper elevation={3} style={{ padding: 12, marginBottom: 12 }}>
                    <Typography variant="h6" gutterBottom>
                      {t`Pattern lock`}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="unlocked-pattern"
                          checked={unlockedPattern}
                          onChange={e => {
                            setUnlockedPattern(e.target.checked)
                          }}
                        />
                      }
                      label={t`Unlocked pattern`}
                    />
                  </Paper>
                )}

                {showRequireUniqueness && (
                  <Paper elevation={3} style={{ padding: 12, marginBottom: 12 }}>
                    <Typography variant="h6" gutterBottom>
                      {t`Require uniqueness`}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="require-uniqueness"
                          checked={requireUniqueness}
                          onChange={e => {
                            setPattern({ ...pattern, must_be_unique: e.target.checked })
                            setRequireUniqueness(prev => !prev)
                          }}
                        />
                      }
                      label={t`Require uniqueness for all templates`}
                    />
                  </Paper>
                )}
              </div>
              <Paper elevation={3} style={{ display: 'flex', flex: 1 }}>
                <div style={{ flex: 1, overflow: 'auto', overflowX: 'hidden', padding: 12 }}>
                  <Typography variant="h6" gutterBottom style={{ padding: '0 4px', height: 32 }}>
                    {t`Configuration`}
                  </Typography>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={pattern.segments?.map(seg => seg.guid) || []}
                      strategy={rectSortingStrategy}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${columns}, 1fr)`,
                          gap: '20px',
                        }}
                      >
                        {pattern.segments?.map(segment => (
                          <SortableSegment key={segment.guid} id={segment.guid}>
                            <Segment
                              segment={segment}
                              onEdit={editSegment}
                              colors={patternTypeColors}
                              onRemove={removeSegment}
                            />
                          </SortableSegment>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </Paper>
            </div>
          </>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
        <CvsDrawer
          visible={Boolean(visible)}
          onClose={() => {
            setVisible(false)
          }}
          title={t`Edit element`}
        >
          <div>
            {selectedSegment && selectedSegment.segment_type === 'constant' && (
              <Constant segment={selectedSegment} updateSegment={updateSegment} />
            )}
            {selectedSegment && selectedSegment.segment_type === 'autonumber' && (
              <Autonumber segment={selectedSegment} updateSegment={updateSegment} />
            )}
            {selectedSegment && selectedSegment.segment_type === 'date' && (
              <DateSeg segment={selectedSegment} updateSegment={updateSegment} />
            )}
            {selectedSegment && selectedSegment.segment_type === 'random' && (
              <Random segment={selectedSegment} updateSegment={updateSegment} />
            )}
            {selectedSegment && selectedSegment.segment_type === 'variable' && (
              <Variable segment={selectedSegment} updateSegment={updateSegment} />
            )}
          </div>
        </CvsDrawer>
      </div>
    </motion.div>
  )
}
